/**
 * `AdminHeader` — the sticky top bar of the admin SPA.
 *
 * Clear visual cues that this is the staff console, not the merchant
 * dashboard: an `ADMIN` mono kicker pinned next to the wordmark and
 * a coral accent line on the bottom border. Right side: signed-in
 * staff email + role pill + theme toggle + logout. No site switcher,
 * no merchant-account dropdown — the admin is intentionally distinct.
 *
 * Responsive:
 *   - <md: a hamburger toggle button appears on the left to open the
 *     mobile drawer (the persistent sidebar is hidden at the same
 *     breakpoint in `Sidebar.tsx`).
 *   - Staff email is hidden on mobile (the role pill still renders so
 *     the staff member can confirm they're signed in as the right role).
 */

import { LogOut, Menu, Moon, Sun } from 'lucide-react';

import type { StaffPrincipalDto } from '@sill/types/api';

import { useC, useTheme } from '../../lib/theme.js';
import { Pill } from '../atoms/Pill.js';
import { ROLE_LABEL } from '../../lib/roles.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface AdminHeaderProps {
  staff: StaffPrincipalDto | null;
  onSignOut: () => void;
  /** Mobile drawer is open — used to hide the menu button when the
   *  drawer is already showing (the drawer has its own close button). */
  mobileNavOpen: boolean;
  /** Called when the staff taps the hamburger button (mobile only). */
  onOpenMobileNav: () => void;
  /** When false the menu button is suppressed (login/verify routes). */
  showMobileNavToggle: boolean;
}

export function AdminHeader({
  staff,
  onSignOut,
  mobileNavOpen,
  onOpenMobileNav,
  showMobileNavToggle,
}: AdminHeaderProps): JSX.Element {
  const C = useC();
  const { theme, setTheme } = useTheme();

  return (
    <header
      className="sticky top-0 z-40 px-3 sm:px-6 py-3"
      style={{
        background: theme === 'dark' ? 'rgba(10,10,10,0.85)' : 'rgba(250,247,240,0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${C.borderSoft}`,
        // Distinct visual: a thin coral accent line under the header so
        // staff never confuse this with the merchant dashboard.
        boxShadow: `inset 0 -2px 0 0 ${C.coralFg}`,
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {showMobileNavToggle && !mobileNavOpen ? (
            <button
              type="button"
              onClick={onOpenMobileNav}
              aria-label="Open navigation"
              aria-expanded={mobileNavOpen}
              className="md:hidden flex items-center justify-center"
              style={{
                width: 40,
                height: 40,
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                color: C.text,
                flexShrink: 0,
              }}
            >
              <Menu size={16} color={C.text} />
            </button>
          ) : null}

          <span
            className="sill-wordmark"
            style={{ fontSize: 32, color: C.text, display: 'inline-block' }}
            aria-label="Sill"
          >
            sill<span className="sill-dot">.</span>
          </span>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.coralFg,
              letterSpacing: 2,
              textTransform: 'uppercase',
              padding: '3px 6px',
              border: `1px solid ${C.coralFg}`,
              borderRadius: 2,
              flexShrink: 0,
            }}
          >
            Admin
          </span>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {staff !== null ? (
            <div className="hidden sm:flex items-center gap-2">
              <span
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.muted,
                }}
              >
                {staff.email}
              </span>
              <Pill tone={staff.role === 'owner' || staff.role === 'admin' ? 'lime' : 'sky'}>
                {ROLE_LABEL[staff.role]}
              </Pill>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label="Toggle theme"
            className="flex items-center justify-center"
            style={{
              width: 40,
              height: 40,
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          >
            {theme === 'dark' ? (
              <Sun size={14} color={C.muted} />
            ) : (
              <Moon size={14} color={C.muted} />
            )}
          </button>

          {staff !== null ? (
            <button
              type="button"
              onClick={onSignOut}
              className="flex items-center justify-center sm:justify-start gap-2"
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
                minHeight: 40,
                minWidth: 40,
                padding: '0 10px',
              }}
              aria-label="Sign out"
            >
              <LogOut size={14} color={C.muted} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );
}
